import React from 'react';
import { User, Calendar, Activity, Heart } from 'lucide-react';

/**
 * Компонент с базовыми параметрами для расчета максимального пульса
 */
const BasicParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  activityLevels 
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {/* Возраст */}
      <div>
        <label htmlFor="age" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Calendar className="w-4 h-4 mr-2 text-red-500" />
          Возраст (лет)
        </label>
        <input
          type="number"
          id="age"
          name="age"
          value={formData.age}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.age ? 'input-error' : ''}`}
          placeholder="Введите ваш возраст"
          min="1"
          max="120"
        />
        {errors.age && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.age}
          </p>
        )}
      </div>

      {/* Пол */}
      <div>
        <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
          <User className="w-4 h-4 mr-2 text-red-500" />
          Пол
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="gender"
              value="male"
              checked={formData.gender === 'male'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Мужской
            </span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="gender"
              value="female"
              checked={formData.gender === 'female'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Женский
            </span>
          </label>
        </div>
        {errors.gender && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.gender}
          </p>
        )}
      </div>

      {/* Пульс в покое */}
      <div className="col-span-full mb-2">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            name="includeRestingHeartRate"
            checked={formData.includeRestingHeartRate}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Учитывать пульс в покое (для формулы Карвонена)
          </span>
        </label>
      </div>

      {formData.includeRestingHeartRate && (
        <div className="col-span-full md:col-span-1 -mt-2">
          <label htmlFor="restingHeartRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Heart className="w-4 h-4 mr-2 text-red-500" />
            Пульс в покое (уд/мин)
          </label>
          <input
            type="number"
            id="restingHeartRate"
            name="restingHeartRate"
            value={formData.restingHeartRate}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.restingHeartRate ? 'input-error' : ''}`}
            placeholder="Введите пульс в покое"
            min="30"
            max="100"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Измерьте пульс утром после пробуждения в положении лежа
          </p>
          {errors.restingHeartRate && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.restingHeartRate}
            </p>
          )}
        </div>
      )}

      {/* Уровень физической активности */}
      <div className="col-span-full md:col-span-1">
        <label htmlFor="activityLevel" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Activity className="w-4 h-4 mr-2 text-red-500" />
          Уровень физической активности
        </label>
        <select
          id="activityLevel"
          name="activityLevel"
          value={formData.activityLevel}
          onChange={handleInputChange}
          className={`form-select w-full ${errors.activityLevel ? 'input-error' : ''}`}
        >
          {activityLevels.map((level) => (
            <option key={level.id} value={level.id}>
              {level.name}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {activityLevels.find(level => level.id === formData.activityLevel)?.description || ''}
        </p>
        {errors.activityLevel && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.activityLevel}
          </p>
        )}
      </div>

      {/* Справка о максимальном пульсе */}
      <div className="col-span-full bg-gray-50 dark:bg-gray-700/20 p-3 rounded-lg border border-gray-200 dark:border-gray-700 mt-2">
        <div className="flex items-start">
          <Heart className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
              Что такое максимальный пульс?
            </h4>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Максимальный пульс (MHR) — это наивысшая частота сердечных сокращений, которую может достичь человек при максимальной физической нагрузке. 
              Знание своего MHR помогает правильно рассчитать интенсивность тренировок и избежать перегрузок. Точно измерить MHR можно только в лабораторных условиях, 
              поэтому обычно используются расчетные формулы на основе возраста и других факторов.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicParametersSection;